// Échantillon et images (modalités) pour le viewer (modèle v2) :
// projects/{projectId}/samples/{sampleId}/images/{imageId}, fichier OME-TIFF à image.storagePath.
import {
  collection, deleteDoc, doc, increment, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { loadOmeTiff } from '@hms-dbmi/viv';
import { EMULATOR_HOSTS, FIREBASE_ENV } from '../components/Firebase/config';
import { db, storage } from './firebase';
import { docData } from './projects';

export function watchSample(projectId, sampleId, onChange, onError) {
  return onSnapshot(doc(db(), 'projects', projectId, 'samples', sampleId),
    snap => onChange(snap.exists() ? docData(snap) : null), onError);
}

export function watchImages(projectId, sampleId, onChange, onError) {
  return onSnapshot(query(collection(db(), 'projects', projectId, 'samples', sampleId, 'images'), orderBy('createdAt')),
    snap => onChange(snap.docs.map(docData)), onError);
}

// URL de lecture du fichier (requêtes HTTP Range par le viewer). Le jeton de téléchargement n'est délivré
// qu'après contrôle des droits par storage.rules. Sur émulateurs, une image peut pointer vers un fichier local
// (devUrl, servi par prototypes/viv-viewer/serve.py) pour tester sans téléverser des Go.
export async function imageUrl(image) {
  if (FIREBASE_ENV === 'emulator' && image.devUrl) return image.devUrl;
  return getDownloadURL(ref(storage(), image.storagePath));
}

// URL d'une tuile produite par le serveur (functions/tiler.py) : toutes les tuiles d'une image partagent un jeton de
// téléchargement (tiles.token, lisible seulement par les membres du projet), donc aucune requête préalable.
// Renvoie urlFor(canal, niveau, x, y).
export function tileUrlFor(image) {
  const { path, token } = image.tiles;
  const host = FIREBASE_ENV === 'emulator'
    ? `http://${EMULATOR_HOSTS.storage.join(':')}` : 'https://firebasestorage.googleapis.com';
  const base = `${host}/v0/b/${storage().app.options.storageBucket}/o/`;
  return (c, level, x, y) => `${base}${encodeURIComponent(`${path}/${c}/${level}/${x}_${y}.jpg`)}?alt=media&token=${token}`;
}

// Relance la préparation de l'affichage (découpage en tuiles par le serveur) : après un échec, ou pour une image
// envoyée avant la mise en place du découpage.
// requestedAt : point de départ du délai au-delà duquel le viewer propose « Relancer ».
export const prepareImage = (projectId, sampleId, imageId) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId, 'images', imageId), {
    status: 'processing', requestedAt: serverTimestamp(),
  });

// Préparation bloquée (aucun signe de vie du serveur) : la remettre en file. Le serveur ne réagit qu'au passage en
// « processing », d'où le détour par « error ».
export async function restartImagePreparation(projectId, sampleId, imageId) {
  const ref = doc(db(), 'projects', projectId, 'samples', sampleId, 'images', imageId);
  await updateDoc(ref, { status: 'error' });
  await updateDoc(ref, { status: 'processing', requestedAt: serverTimestamp() });
}

// Réglages d'affichage par défaut de l'image (LUT, couleur, inversion, contraste), partagés entre utilisateurs.
export const saveImageDisplay = (projectId, sampleId, imageId, display) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId, 'images', imageId), { display });

// Nom affiché de l'image (viewer, listes), partagé.
export const renameImage = (projectId, sampleId, imageId, label) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId, 'images', imageId), { label });

// ------------------------------------------------------------------ ajout d'images

// Modalités connues, reconnues dans les noms de fichiers de la plateforme (ex. « …_epi_280_aligned.ome-004.tif »).
export const MODALITIES = [
  { key: 'epi280', label: 'Deep UV fluorescence (epi 280)', pattern: /epi[_-]?280/i },
  { key: 'trans265', label: 'Deep UV absorbance (trans 265)', pattern: /trans[_-]?265/i },
  { key: 'trans375', label: 'UV scattering (trans 375)', pattern: /trans[_-]?375/i },
  { key: 'trans530', label: 'Visible light scattering (trans 530)', pattern: /trans[_-]?530/i },
  { key: 'multiFluo', label: 'Multispectral visible fluorescence', pattern: /multi[_-]?fluo/i },
  { key: 'HES', label: 'HES', pattern: /(^|[^a-z])(hes?|h&e)([^a-z]|$)/i },  // « _hes_ », « _he. », « H&E »
  { key: 'shg', label: 'SHG', pattern: /(^|[^a-z])shg([^a-z]|$)/i },
];

// Modalité d'après le nom du fichier : liste connue, sinon toute longueur d'onde de la plateforme
// (« epi_405 », « trans530 »…) ; null si rien ne correspond.
export function guessModality(fileName) {
  const known = MODALITIES.find(m => m.pattern.test(fileName));
  if (known) return known;
  const match = fileName.match(/(epi|trans)[_-]?(\d{3})/i);
  if (!match) return null;
  const [, kind, nm] = match;
  return kind.toLowerCase() === 'epi'
    ? { key: `epi${nm}`, label: `Fluorescence (epi ${nm})` }
    : { key: `trans${nm}`, label: `Transmission (trans ${nm})` };
}

// Nom proposé pour une modalité (nom effacé au renommage).
export const defaultImageLabel = modality => guessModality(modality)?.label || modality;

// Lit uniquement l'en-tête du fichier (quelques Ko) pour vérifier que c'est un OME-TIFF pyramidal lisible par le
// viewer, avant d'envoyer des Go. Renvoie { width, height, levels, channels, rgb, dtype } ou lève une erreur explicite.

// Dimensions d'un niveau Viv d'après ses étiquettes d'axes (la dernière dimension est la couleur pour une image RGB).
export function pixelSize(source) {
  const { shape, labels } = source;
  const rgb = [3, 4].includes(shape[shape.length - 1]) && labels[labels.length - 1] !== 'x';
  return { width: shape[labels.indexOf('x')], height: shape[labels.indexOf('y')], rgb };
}

// Longueur physique en µm (unités OME : µm, nm, mm…), null si inconnue.
const MICRONS = { 'µm': 1, 'μm': 1, um: 1, nm: 1e-3, 'Å': 1e-4, mm: 1e3, cm: 1e4, m: 1e6 };
export const toMicrons = (size, unit = 'µm') => (size > 0 && MICRONS[unit] ? size * MICRONS[unit] : null);

export async function inspectOmeTiff(file) {
  if (!/\.tiff?$/i.test(file.name)) throw new Error('The file must be an OME-TIFF (.tif or .tiff).');
  let res;
  try {
    res = await loadOmeTiff(file);
  } catch (err) {
    throw new Error(`Unreadable as an OME-TIFF (${err.message}).`);
  }
  const { data } = Array.isArray(res) ? res[0] : res;
  const base = data[0];
  const { width, height, rgb } = pixelSize(base);
  const channels = rgb ? 1 : base.shape[base.labels.indexOf('c')] || 1;
  if (data.length < 2 && Math.max(width, height) > 4096) {
    throw new Error('Not a pyramidal image: the viewer needs a pyramid (downsampled levels) for large images.');
  }
  // Taille de pixel des métadonnées OME (échelle du viewer ; les tuiles du serveur ne la portent pas).
  const physical = base.meta?.physicalSizes?.x;
  const pixelSizeUm = physical ? toMicrons(physical.size, physical.unit) : null;
  return { width, height, levels: data.length, channels, rgb, dtype: base.dtype, ...(pixelSizeUm ? { pixelSizeUm } : {}) };
}

// Envoie une image : crée sa fiche (statut « uploading »), téléverse le fichier par morceaux (reprise automatique),
// puis la passe en « processing » (le serveur la découpe en tuiles, puis la passe en « ready ») et incrémente le
// compteur d'images de l'échantillon.
// Renvoie { imageId, task, done } : task permet pause / reprise / annulation, done se résout à la fin.
export function uploadImage(profile, projectId, sampleId, file, { modality, label, info }, onProgress) {
  const imageRef = doc(collection(db(), 'projects', projectId, 'samples', sampleId, 'images'));
  const storagePath = `projects/${projectId}/samples/${sampleId}/images/${imageRef.id}.ome.tif`;
  let task = null;
  const done = (async () => {
    await setDoc(imageRef, {
      modality, label, storagePath, status: 'uploading', originalName: file.name, size: file.size, ...info,
      createdBy: profile.uid, createdAt: serverTimestamp(),
    });
    task = uploadBytesResumable(ref(storage(), storagePath), file, { contentType: 'image/tiff' });
    task.on('state_changed', snap => onProgress?.(snap.bytesTransferred / snap.totalBytes, snap.state));
    try {
      await task;
    } catch (err) {
      await updateDoc(imageRef, { status: err.code === 'storage/canceled' ? 'canceled' : 'error' }).catch(() => {});
      throw err;
    }
    await updateDoc(imageRef, { status: 'processing', uploadedAt: serverTimestamp(), requestedAt: serverTimestamp() });
    await updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), {
      imageCount: increment(1), updatedAt: serverTimestamp(),
    });
    return imageRef.id;
  })();
  return { imageId: imageRef.id, task: () => task, done };
}

// Supprime une image (modalité) : fiche d'abord (les règles n'autorisent que son auteur ou un admin), puis fichier
// original s'il existe (un envoi interrompu n'en laisse pas forcément). Le serveur (onimagedelete) supprime ensuite
// ses tuiles, et l'original s'il reste. Décrémente le compteur de l'échantillon si le fichier avait été envoyé.
export async function deleteImage(projectId, sampleId, image) {
  await deleteDoc(doc(db(), 'projects', projectId, 'samples', sampleId, 'images', image.id));
  await deleteObject(ref(storage(), image.storagePath)).catch(err => {
    if (err.code !== 'storage/object-not-found') throw err;
  });
  if (image.uploadedAt || image.status === 'ready') {
    await updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), {
      imageCount: increment(-1), updatedAt: serverTimestamp(),
    });
  }
}
